import { useEffect, useId, useRef, useSyncExternalStore, type RefObject } from "react";

/*
  A képernyő aljára tapadó sávok egymás fölötti rendje, alulról felfelé:
  1. az átmeneti sávok (a tömeges műveletek sávja, `useBottomLayer`): a saját gesztusod hozza
     elő őket, a helyükön jelennek meg;
  2. a tartós lebegő sáv (`FloatingBar`, pl. a nézetváltó): ha alatta átmeneti sáv jelenik
     meg, fölé húzódik (`useBottomLayersReach`);
  3. a toastok: mindig a verem tetején, a `--bottom-stack-top` fölött (`ui/sonner.tsx`). A
     toast egy szerverválaszra, bármikor jön, ezért az alatta lévő sáv miatta nem mozdul: egy
     épp megcélzott fül nem ugorhat el az ujj alól.
  Minden érték px a képernyő aljától; a rétegek egymásról nem tudnak, csak jelentenek.
*/

/** Rés a verem két szomszédos eleme között, px */
export const BOTTOM_STACK_GAP = 12;

/**
 * Azonosító -> px nyilvántartás, amely mindig a legnagyobb értéket teszi közzé (`onChange`,
 * csak ha az változott; 0, ha üres). Egész px-re kerekít, a `null` vagy 0 kivesz.
 */
function createMaxRegistry(onChange: (max: number) => void) {
  const values = new Map<string, number>();
  let max = 0;
  return {
    set(id: string, value: number | null) {
      const next = value === null ? 0 : Math.round(value);
      if (next > 0) {
        if (values.get(id) === next) return;
        values.set(id, next);
      } else if (!values.delete(id)) {
        return;
      }
      const nextMax = Math.max(0, ...values.values());
      if (nextMax === max) return;
      max = nextMax;
      onChange(max);
    },
    get max() {
      return max;
    },
  };
}

function setRootPx(name: string, px: number) {
  const root = document.documentElement;
  if (px > 0) root.style.setProperty(name, `${px}px`);
  else root.style.removeProperty(name);
}

/** Akik az átmeneti sávok tetejét figyelik (a lebegő sávok) */
const reachListeners = new Set<() => void>();

/** Az átmeneti sávok felső éle */
const layers = createMaxRegistry(() => {
  reachListeners.forEach((listener) => listener());
  writeStackTop();
});
/** A lebegő sávok MOSTANI felső éle (a húzódással együtt) */
const floatingTops = createMaxRegistry(writeStackTop);
/**
 * A lebegő sávok nyugalmi helyigénye (felső él + rés): a lábléc alsó térköze és a `html`
 * `scroll-padding-bottom`-ja olvassa. Az átmeneti sávok idejére nem nő: az oldal hossza nem
 * ugrálhat velük.
 */
const floatingSpaces = createMaxRegistry((max) => setRootPx("--floating-bar-space", max));

/** A verem teteje: efölött jelennek meg a toastok */
function writeStackTop() {
  setRootPx("--bottom-stack-top", Math.max(layers.max, floatingTops.max));
}

/**
 * Egy átmeneti sáv felső éle a képernyő aljától, px-ben (`null` vagy 0, ha nem látszik).
 * Az `id` sávonként egyedi (`useId`): egy lista több példányban is élhet egyszerre.
 */
export function setBottomLayer(id: string, top: number | null) {
  layers.set(id, top);
}

/**
 * Egy tartós lebegő sáv jelentése: `rest` a nyugalmi felső éle, `top` a mostani (a
 * húzódással együtt); `null`, ha a sáv eltűnt.
 */
export function reportFloatingBar(id: string, edges: { rest: number; top: number } | null) {
  floatingSpaces.set(id, edges && edges.rest + BOTTOM_STACK_GAP);
  floatingTops.set(id, edges && edges.top);
}

function subscribeReach(listener: () => void) {
  reachListeners.add(listener);
  return () => {
    reachListeners.delete(listener);
  };
}

/** Ameddig a látható átmeneti sávok közül a legmagasabb felér (0: egy sincs). */
export function useBottomLayersReach(): number {
  return useSyncExternalStore(subscribeReach, () => layers.max);
}

/**
 * Egy `position: fixed`, alulra tapadó elem nyugalmi helyének figyelése: a kiszámolt `bottom`
 * és a magasság, nem a `getBoundingClientRect`. A be- és kicsúszó animáció transzformja így nem
 * mozgatja az értéket: aki ehhez igazodik, egyből a végleges helyre indul. Méretváltozáskor és
 * ablakméret-váltáskor újramér; a visszaadott függvény leállítja a figyelést.
 */
export function observeFixedBottom(
  element: HTMLElement,
  onMeasure: (edge: { bottom: number; height: number }) => void,
): () => void {
  const measure = () =>
    onMeasure({
      bottom: parseFloat(getComputedStyle(element).bottom) || 0,
      height: element.offsetHeight,
    });
  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(element);
  window.addEventListener("resize", measure);
  return () => {
    observer.disconnect();
    window.removeEventListener("resize", measure);
  };
}

/**
 * Egy `position: fixed`, alulra tapadó elemet jelent be átmeneti sávként, amíg `active`; a
 * felső éle a nyugalmi helyéből jön (`observeFixedBottom`).
 */
export function useBottomLayer<T extends HTMLElement>(active: boolean): RefObject<T | null> {
  const id = useId();
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!active || !element) return;
    const stop = observeFixedBottom(element, ({ bottom, height }) => setBottomLayer(id, bottom + height));
    return () => {
      stop();
      // A kicsúszó animáció még tart, de a hely már szabad: a sáv ne várja meg
      setBottomLayer(id, null);
    };
  }, [active, id]);

  return ref;
}
